import { api } from "./client";
import type { PagedQuery, PagedResult } from "@/types/api";

/**
 * Tasks Module v1.0 — frontend client. Mirrors the backend
 * <c>WorkTask</c> aggregate (the C# class is named <c>WorkTask</c> to
 * avoid collision with <c>System.Threading.Tasks.Task</c>; user-facing
 * labels — DTOs, API routes, UI text — keep the noun "Task").
 *
 * Distinct from <c>tasksApi</c> in <c>./tasks.ts</c>, which still drives
 * the Broker CRM v3.0 BrokerTask pages.
 */

export type WorkTaskStatus = "Open" | "InProgress" | "Done";
export type WorkTaskPriority = "Low" | "Medium" | "High" | "Critical";
export type WorkTaskRelatedEntityType =
  | "None" | "Deal" | "Broker" | "Report" | "Loan" | "Document" | "Lead"
  | "Person" | "Entity" | "Business" | "SpvPool" | "Spv"
  // SPV Pool v1.0 §2.3 — Investor + service-provider task targets.
  | "Investor" | "IndentureTrustee" | "TrustCompany" | "PlacementAgent"
  | "BackupServicer"
  // Reunderwriting Module (Tier 3) PR 3 — case-detail Tasks tab.
  | "ReunderwritingCase"
  // Vendor Program v2 PR 1 — vendor-detail Tasks tab.
  | "Vendor"
  // WS8 — TPS provider detail Tasks tab.
  | "Tps"
  // Attach-record expansion — Local Enforcer + Portal Account (External
  // Account) tabs added in the same PR; mirror WorkTaskRelatedEntityType.cs.
  | "LocalEnforcer"
  | "PortalUser"
  // Brokerage CRM parity — staff Brokerage firm detail Tasks tab.
  | "Brokerage"
  // Borrower/Seller party surfaces — party detail Tasks tabs.
  | "Borrower"
  | "Seller"
  // Buyer CRM (PR 2a) — PascalCase, mirroring WorkTaskRelatedEntityType.
  | "BuyerLead"
  // Buyer programme (PR 2) — the listing catalog record and the buyer↔listing
  // submission, where most pipeline follow-up work actually lives.
  | "Listing"
  | "BuyerSubmission";

export type WorkTaskActivityType =
  | "Created" | "StatusChanged" | "AssigneeChanged" | "PriorityChanged"
  | "DueDateChanged" | "TitleChanged" | "RelatedEntityChanged" | "Reopened"
  | "Deleted" | "Restored" | "DescriptionChanged" | "TagsChanged"
  // Tasks Module v2 — participants added / removed / multiuser toggled.
  | "ParticipantsChanged";

export interface TaskTagDto {
  id: string;
  tagCode: string;
  displayName: string;
  color: string | null;
  sortOrder: number;
  isActive: boolean;
  isSystemReserved: boolean;
}

export interface TaskTagCreateDto {
  tagCode: string;
  displayName: string;
  color?: string | null;
  sortOrder?: number;
}

export interface TaskTagUpdateDto {
  displayName: string;
  color?: string | null;
  sortOrder?: number | null;
  isActive?: boolean | null;
}

export interface TaskAssigneeDto {
  userId: string;
  displayName: string;
}

export interface TaskRelatedEntitySummaryDto {
  type: WorkTaskRelatedEntityType;
  id: string;
  displayName: string;
  detailUrl: string | null;
}

export interface WorkTaskDto {
  id: string;
  title: string;
  description: string | null;
  status: WorkTaskStatus;
  priority: WorkTaskPriority;
  dueDate: string | null;
  assignee: TaskAssigneeDto | null;
  createdBy: TaskAssigneeDto;
  completedBy: TaskAssigneeDto | null;
  completedDate: string | null;
  relatedEntityType: WorkTaskRelatedEntityType;
  relatedEntityId: string | null;
  relatedEntity: TaskRelatedEntitySummaryDto | null;
  tags: TaskTagDto[];
  createdAt: string;
  modifiedAt: string | null;
  isDeleted: boolean;
  // Tasks Module v2 — multiuser (shared) tasks + progress.
  isMultiuser: boolean;
  progressPercent: number;
  participants: TaskAssigneeDto[];
}

export interface CreateWorkTaskDto {
  title: string;
  description?: string | null;
  priority?: WorkTaskPriority | null;
  dueDate?: string | null;
  assigneeId?: string | null;
  relatedEntityType?: WorkTaskRelatedEntityType | null;
  relatedEntityId?: string | null;
  tagIds?: string[] | null;
  // Tasks Module v2 — set both to create a shared task.
  isMultiuser?: boolean;
  participantIds?: string[];
}

export interface UpdateWorkTaskDto {
  title?: string | null;
  description?: string | null;
  priority?: WorkTaskPriority | null;
  dueDate?: string | null;
  clearDueDate?: boolean;
  relatedEntityType?: WorkTaskRelatedEntityType | null;
  relatedEntityId?: string | null;
  clearRelatedEntity?: boolean;
  tagIds?: string[] | null;
}

export interface TaskCommentDto {
  id: string;
  taskId: string;
  authorId: string;
  authorDisplayName: string;
  content: string;
  createdAt: string;
  modifiedAt: string | null;
  isDeleted: boolean;
}

export interface TaskActivityLogDto {
  id: string;
  taskId: string;
  actorId: string;
  actorDisplayName: string;
  activityType: WorkTaskActivityType;
  previousValue: string | null;
  newValue: string | null;
  createdAt: string;
}

export interface WorkTaskListQuery extends PagedQuery {
  status?: WorkTaskStatus;
  priority?: WorkTaskPriority;
  assigneeId?: string;
  createdById?: string;
  /**
   * Convenience filter. <c>"me"</c> = caller is assignee (My open);
   * <c>"team"</c> = caller's multiuser tasks (owner OR participant).
   */
  scope?: "me" | "team";
  dueDateFrom?: string;
  dueDateTo?: string;
  tagId?: string;
  relatedEntityType?: WorkTaskRelatedEntityType;
  relatedEntityId?: string;
  includeDeleted?: boolean;
}

export const workTasksApi = {
  // Tasks — backend mounts the controller at /api/work-tasks so its routes
  // do not collide with the pre-existing Broker CRM v3.0
  // BrokerTasksController which still owns /api/tasks/*. The user-facing
  // label is still "Tasks" — only the URL mirrors the C# entity name.
  list: (query?: WorkTaskListQuery) =>
    api.get<PagedResult<WorkTaskDto>>("/work-tasks", query),

  get: (id: string) => api.get<WorkTaskDto>(`/work-tasks/${id}`),

  create: (payload: CreateWorkTaskDto) =>
    api.post<WorkTaskDto>("/work-tasks", payload),

  update: (id: string, payload: UpdateWorkTaskDto) =>
    api.patch<WorkTaskDto>(`/work-tasks/${id}`, payload),

  changeStatus: (id: string, status: WorkTaskStatus) =>
    api.post<WorkTaskDto>(`/work-tasks/${id}/status`, { status }),

  reassign: (id: string, assigneeId: string | null) =>
    api.post<WorkTaskDto>(`/work-tasks/${id}/assignee`, { assigneeId }),

  // Tasks Module v2 — progress slider (In-progress only; 100 auto-completes).
  setProgress: (id: string, percent: number) =>
    api.post<WorkTaskDto>(`/work-tasks/${id}/progress`, { percent }),

  // Tasks Module v2 — replace the full participant list (empty = un-share).
  setParticipants: (id: string, participantIds: string[]) =>
    api.put<WorkTaskDto>(`/work-tasks/${id}/participants`, { participantIds }),

  delete: (id: string) => api.delete<void>(`/work-tasks/${id}`),

  restore: (id: string) => api.post<void>(`/work-tasks/${id}/restore`),

  // Comments ------------------------------------------------------------
  listComments: (taskId: string) =>
    api.get<TaskCommentDto[]>(`/work-tasks/${taskId}/comments`),

  addComment: (taskId: string, content: string) =>
    api.post<TaskCommentDto>(`/work-tasks/${taskId}/comments`, { content }),

  updateComment: (taskId: string, commentId: string, content: string) =>
    api.patch<TaskCommentDto>(`/work-tasks/${taskId}/comments/${commentId}`, { content }),

  deleteComment: (taskId: string, commentId: string) =>
    api.delete<void>(`/work-tasks/${taskId}/comments/${commentId}`),

  // Activity ------------------------------------------------------------
  getActivity: (id: string, skip = 0, take = 50) =>
    api.get<PagedResult<TaskActivityLogDto>>(`/work-tasks/${id}/activity`, { skip, take }),

  // Related-entity ------------------------------------------------------
  listByRelated: (type: WorkTaskRelatedEntityType, entityId: string) =>
    api.get<WorkTaskDto[]>(`/work-tasks/by-related/${type}/${entityId}`),

  /**
   * Open-task count for an entity's Tasks tab badge. Detail pages want the
   * number, not the rows — reading it from `listByRelated` costs the whole
   * task list on every page load. Only surfaces that render rows (see
   * TaskListMini) should call `listByRelated`.
   */
  countOpenByRelated: (type: WorkTaskRelatedEntityType, entityId: string) =>
    api.get<number>(`/work-tasks/by-related/${type}/${entityId}/open-count`),

  // Dashboard widget ----------------------------------------------------
  myOpen: (limit = 5) =>
    api.get<WorkTaskDto[]>("/work-tasks/my-open", { limit }),

  // Tags ----------------------------------------------------------------
  listTags: () => api.get<TaskTagDto[]>("/task-tags"),

  listAdminTags: () => api.get<TaskTagDto[]>("/admin/task-tags"),

  createTag: (payload: TaskTagCreateDto) =>
    api.post<TaskTagDto>("/admin/task-tags", payload),

  updateTag: (id: string, payload: TaskTagUpdateDto) =>
    api.patch<TaskTagDto>(`/admin/task-tags/${id}`, payload),

  deactivateTag: (id: string) => api.delete<void>(`/admin/task-tags/${id}`),
};

// ----------------------------------------------------------------------
// PR 3 — additions
// ----------------------------------------------------------------------

export interface UserSearchResultDto {
  id: string;
  displayName: string;
  email: string;
  avatarUrl: string | null;
}

// Tasks v1.1 — admin tag manager.

export interface TaskTagAdminDto {
  id: string;
  name: string;
  color: string | null;
  description: string | null;
  isArchived: boolean;
  displayOrder: number;
  totalUsageCount: number;
  openUsageCount: number;
  modifiedDate: string;
  modifiedByDisplayName: string | null;
}

export interface CreateTaskTagAdminDto {
  name: string;
  color?: string | null;
  description?: string | null;
}

export interface UpdateTaskTagAdminDto {
  name?: string | null;
  color?: string | null;
  description?: string | null;
}

export interface MergeTaskTagsResultDto {
  reassignedCount: number;
  deduplicatedCount: number;
}

export const adminWorkTaskTagsApi = {
  list: (includeArchived = false) =>
    api.get<TaskTagAdminDto[]>("/admin/work-tasks/tags", { includeArchived }),
  create: (payload: CreateTaskTagAdminDto) =>
    api.post<TaskTagAdminDto>("/admin/work-tasks/tags", payload),
  update: (id: string, payload: UpdateTaskTagAdminDto) =>
    api.patch<TaskTagAdminDto>(`/admin/work-tasks/tags/${id}`, payload),
  archive: (id: string) =>
    api.post<void>(`/admin/work-tasks/tags/${id}/archive`),
  restore: (id: string) =>
    api.post<void>(`/admin/work-tasks/tags/${id}/restore`),
  merge: (sourceId: string, targetId: string) =>
    api.post<MergeTaskTagsResultDto>("/admin/work-tasks/tags/merge", { sourceId, targetId }),
};

/** Drives the @mention autocomplete on the comment editor. */
export const userSearchApi = {
  search: (q: string, limit = 10) =>
    api.get<UserSearchResultDto[]>("/users/search", { q, limit }),
};

/**
 * Build the admin CSV-export URL with the same query params the list
 * endpoint takes. Returned as a string so the caller can wire it to a
 * native <a download> element — that is the lowest-friction download
 * path with cookie-based auth (the api.get wrapper would unwrap the
 * envelope and choke on the raw text/csv body).
 */
export function buildTaskExportUrl(query?: WorkTaskListQuery): string {
  const base = (import.meta.env.VITE_API_BASE_URL ?? "/api") + "/admin/work-tasks/export";
  if (!query) return base;
  const params = new URLSearchParams();
  for (const [k, v] of Object.entries(query as Record<string, unknown>)) {
    if (v !== undefined && v !== null && v !== "") {
      params.append(k, String(v));
    }
  }
  const qs = params.toString();
  return qs ? `${base}?${qs}` : base;
}
